import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, Plug, Trash2 } from 'lucide-react';
import authService from '../../services/api/authService';
import { McpToken } from '../../types';
import { buildClientGuides, getMcpConnection, McpClientId } from '../../utils/mcpConnection';
import { TaskyAlert, TaskyButton, TaskyFormGroup, TaskyInput } from '../tasky';

/**
 * MCP 用アクセストークンの発行・確認・失効を行うカード。
 *
 * Google ログインのユーザーはパスワードがランダム値のため
 * メールアドレス + パスワードでは MCP にログインできない。
 * トークンは認証方法によらず使えるので、全アカウント共通の入口になる。
 */

const CARD =
  'bg-white border border-warm-border rounded-tasky-md shadow-[0_1px_3px_rgba(29,25,39,0.04)]';
const CARD_CONTENT = 'p-6 max-[640px]:p-4';
const SECTION_TITLE = 'text-[16px] font-semibold text-dark m-0 mb-4';
const DESCRIPTION = 'text-[14px] text-gray-500 leading-relaxed mb-4';
const ROW =
  'flex items-center justify-between gap-3 py-3 border-b border-b-warm-border ' +
  'last-of-type:border-b-0 max-[640px]:flex-col max-[640px]:items-start';
const ROW_LABEL = 'text-[14px] font-medium text-dark m-0 [overflow-wrap:anywhere]';
const ROW_META = 'text-[13px] text-gray-500 m-0 mt-1';
const EMPTY = 'text-[14px] text-gray-500 py-3 m-0';
const TOKEN_BOX =
  'font-mono text-[13px] bg-warm-bg border border-warm-border rounded-tasky-sm ' +
  'p-3 [overflow-wrap:anywhere] select-all';
const GUIDE = 'mt-6 pt-6 border-t border-warm-border';
const GUIDE_TITLE = 'text-[14px] font-semibold text-dark m-0 mb-3';
const GUIDE_STEPS = 'text-[14px] text-gray-500 leading-relaxed m-0 pl-5 list-decimal';
const GUIDE_STEP = 'mb-3 last:mb-0';
const GUIDE_CODE =
  'font-mono text-[12px] bg-warm-bg border border-warm-border rounded-tasky-sm ' +
  'p-3 mt-2 m-0 whitespace-pre-wrap [overflow-wrap:anywhere] select-all';
const GUIDE_INLINE_CODE = 'font-mono text-[13px] bg-warm-bg px-1 py-0.5 rounded-tasky-sm';
const GUIDE_TABS = 'flex gap-1 border-b border-warm-border mb-4 max-[640px]:flex-wrap';
const GUIDE_TAB =
  'px-3 py-2 text-[13px] font-medium bg-transparent border-0 border-b-2 cursor-pointer ' +
  '-mb-px [transition:color_0.15s]';
const GUIDE_TAB_ON = 'border-b-primary text-primary';
const GUIDE_TAB_OFF = 'border-b-transparent text-gray-500 hover:text-dark';
const GUIDE_TARGET = 'text-[13px] text-gray-500 m-0 mt-1';
const GUIDE_DL = 'mt-4 text-[13px] text-gray-500';
const GUIDE_DL_ROW = 'flex gap-3 py-1 max-[640px]:flex-col max-[640px]:gap-0';
const GUIDE_DT = 'w-[100px] shrink-0 text-dark';
const GUIDE_DD = 'm-0 font-mono [overflow-wrap:anywhere]';

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ja-JP');
};

const McpTokenCard: React.FC = () => {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [label, setLabel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // 発行直後の平文トークン。画面を離れると二度と取得できない。
  const [issuedToken, setIssuedToken] = useState('');
  const [copiedKey, setCopiedKey] = useState<'token' | 'command' | ''>('');

  const [activeClient, setActiveClient] = useState<McpClientId>('claude');

  const connection = useMemo(() => getMcpConnection(), []);
  // 発行直後だけ本物のトークン入りの手順を出す。再表示できないので、
  // ここでコピーできることがこの画面の実質的な出口になる。
  const guides = useMemo(
    () => (connection ? buildClientGuides(connection, issuedToken) : []),
    [connection, issuedToken]
  );
  const guide = guides.find((g) => g.id === activeClient) ?? guides[0];

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      setTokens(await authService.listMcpTokens());
    } catch {
      setLoadError('MCPトークンの取得に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setSubmitError('用途がわかる名前を入力してください（例: MacBook）');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await authService.createMcpToken({ label: trimmed });
      setIssuedToken(response.token);
      setLabel('');
      setCopiedKey('');
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'トークンの発行に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevoke = async (token: McpToken) => {
    setSubmitError('');
    try {
      await authService.revokeMcpToken(token.id);
      await load();
    } catch {
      setSubmitError('トークンの失効に失敗しました。');
    }
  };

  const handleCopy = async (key: 'token' | 'command', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
    } catch {
      // クリップボードが使えない環境では手動選択に任せる（select-all を付けてある）
      setCopiedKey('');
    }
  };

  return (
    <div className={CARD} style={{ marginTop: 24 }} data-tutorial="account-settings-mcp-token">
      <div className={CARD_CONTENT}>
        <h2 className={SECTION_TITLE}>
          <Plug className="inline w-[18px] h-[18px] mr-2 -mt-0.5 text-primary" />
          MCP アクセストークン
        </h2>

        <p className={DESCRIPTION}>
          Claude Code などの MCP クライアントから Tasky を操作するためのトークンです。
          Google ログインをお使いの場合もこちらから発行できます。
          トークンは商品の登録・バナー生成など MCP が使う機能に限定されており、
          支払い情報などその他の操作には使えません。
        </p>

        {loadError && <TaskyAlert tone="error">{loadError}</TaskyAlert>}
        {submitError && <TaskyAlert tone="error">{submitError}</TaskyAlert>}

        {issuedToken && (
          <div style={{ marginBottom: 16 }}>
            <TaskyAlert tone="warning">
              このトークンが表示されるのは一度きりです。今すぐコピーして保管してください。
            </TaskyAlert>
            <div className={TOKEN_BOX}>{issuedToken}</div>
            <div style={{ marginTop: 8 }}>
              <TaskyButton variant="secondary" onClick={() => handleCopy('token', issuedToken)}>
                <Copy className="w-4 h-4 mr-1" />
                {copiedKey === 'token' ? 'コピーしました' : 'コピー'}
              </TaskyButton>
            </div>
          </div>
        )}

        <TaskyFormGroup label="新しいトークンを発行">
          <div className="flex gap-2 max-[640px]:flex-col">
            <TaskyInput
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="用途がわかる名前（例: MacBook）"
              maxLength={50}
              disabled={isSubmitting}
            />
            <TaskyButton onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : '発行する'}
            </TaskyButton>
          </div>
        </TaskyFormGroup>

        {isLoading ? (
          <p className={EMPTY}>
            <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
            読み込み中...
          </p>
        ) : tokens.length === 0 ? (
          <p className={EMPTY}>発行済みのトークンはありません。</p>
        ) : (
          <div>
            {tokens.map((token) => (
              <div key={token.id} className={ROW}>
                <div>
                  <p className={ROW_LABEL}>{token.label}</p>
                  <p className={ROW_META}>
                    最終利用: {formatDate(token.last_used_at)} / 有効期限:{' '}
                    {formatDate(token.expires_at)}
                  </p>
                </div>
                <TaskyButton variant="secondary" onClick={() => handleRevoke(token)}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  失効
                </TaskyButton>
              </div>
            ))}
          </div>
        )}

        {connection && guide && (
          <div className={GUIDE}>
            <h3 className={GUIDE_TITLE}>MCP クライアントへの登録方法</h3>

            <div className={GUIDE_TABS} role="tablist">
              {guides.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={g.id === activeClient}
                  className={`${GUIDE_TAB} ${g.id === activeClient ? GUIDE_TAB_ON : GUIDE_TAB_OFF}`}
                  onClick={() => {
                    setActiveClient(g.id);
                    setCopiedKey('');
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <ol className={GUIDE_STEPS}>
              <li className={GUIDE_STEP}>
                上の「新しいトークンを発行」でトークンを作り、表示された文字列をコピーします。
              </li>
              <li className={GUIDE_STEP}>
                次の内容を {guide.target}します。
                <pre className={GUIDE_CODE}>{guide.snippet}</pre>
                <div style={{ marginTop: 8 }}>
                  <TaskyButton
                    variant="secondary"
                    onClick={() => handleCopy('command', guide.snippet)}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    {copiedKey === 'command' ? 'コピーしました' : 'コピー'}
                  </TaskyButton>
                </div>
                {!issuedToken && (
                  <p className={ROW_META}>
                    ※ <code className={GUIDE_INLINE_CODE}>&lt;発行したトークン&gt;</code>{' '}
                    は自分のトークンに置き換えてください。
                  </p>
                )}
              </li>
              <li className={GUIDE_STEP}>
                {guide.verify}
                {guide.note && <p className={ROW_META}>※ {guide.note}</p>}
              </li>
            </ol>

            <div className={GUIDE_DL}>
              <p className={GUIDE_TARGET} style={{ marginTop: 0, marginBottom: 4 }}>
                上記以外のクライアントに登録する場合は、この情報を設定してください。
              </p>
              <div className={GUIDE_DL_ROW}>
                <span className={GUIDE_DT}>サーバー URL</span>
                <p className={GUIDE_DD}>{connection.url}</p>
              </div>
              <div className={GUIDE_DL_ROW}>
                <span className={GUIDE_DT}>通信方式</span>
                <p className={GUIDE_DD}>HTTP（Streamable HTTP）</p>
              </div>
              <div className={GUIDE_DL_ROW}>
                <span className={GUIDE_DT}>認証ヘッダー</span>
                <p className={GUIDE_DD}>Authorization: Bearer &lt;発行したトークン&gt;</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default McpTokenCard;
